import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import { siteConfig } from '../data/mock';
import Logo from "../assets/Logo.svg";


const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const location = useLocation();

  const [dropdownTimeout, setDropdownTimeout] = useState(null);

  const handleMouseEnter = (name) => {
    if (dropdownTimeout) {
      clearTimeout(dropdownTimeout);
      setDropdownTimeout(null);
    }
    setOpenDropdown(name);
  };

  const handleMouseLeave = () => {
    const timeout = setTimeout(() => {
      setOpenDropdown(null);
    }, 400);

    setDropdownTimeout(timeout);
  };


  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Home', path: '/' },

    {
      name: 'About Us',
      dropdown: [
        { name: 'About Centre', path: '/about' },
        { name: 'Team', path: '/team' },
      ]
    },

    {
      name: 'Academics',
      dropdown: [
        { name: 'Programs', path: '/academics' },
        { name: 'Publications', path: '/publications' },
      ]
    },

    { name: 'Activities & Gallery', path: '/activities' },
    { name: 'Media & Outreach', path: '/media' },

    { name: 'Work With Us', path: '/work-with-us' },

    { name: 'Contact', path: '/contact' }
  ];


  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled
        ? 'bg-white/90 backdrop-blur-lg shadow-[0_8px_30px_rgb(0,0,0,0.04)]'
        : 'bg-white/80'
        }`}
    >
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-20">

          <Link to="/" className="flex items-center space-x-3 group">
            <div className="w-16 h-16 bg-gradient-to-br from-primary to-orange-600 rounded-full flex items-center justify-center shadow-lg group-hover:scale-105 transition-transform">
              <img
                src={Logo}
                alt="VAC-IKS Logo"
                className="w-18 h-18 rounded-full object-cover"
              />
            </div>

            <div className="hidden md:block">
              <div className="font-bold text-foreground text-lg leading-tight">
                {siteConfig.shortName}
              </div>
              <div className="text-xs text-muted-foreground">
                Maitreyi College
              </div>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center space-x-2">

            {navLinks.map((link, i) => (
              <div
                key={i}
                className="relative"
                onMouseEnter={() => handleMouseEnter(link.name)}
                onMouseLeave={handleMouseLeave}
              >

                {/* NORMAL LINK */}
                {!link.dropdown && (
                  <Link
                    to={link.path}
                    className={`px-3 py-2 text-sm font-medium rounded-lg transition-all ${link.highlight
                      ? 'bg-primary text-white hover:bg-orange-600 shadow'
                      : location.pathname === link.path
                        ? 'text-primary bg-primary/10'
                        : 'text-foreground hover:text-primary hover:bg-primary/5'
                      }`}
                  >
                    {link.name}
                  </Link>
                )}

                {/* DROPDOWN */}
                {link.dropdown && (
                  <>
                    <div
                      className={`flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-lg cursor-pointer transition-all ${link.dropdown.some(d => d.path === location.pathname)
                        ? 'text-primary bg-primary/10'
                        : 'text-foreground hover:text-primary hover:bg-primary/5'
                        }`}
                    >
                      {link.name}
                      <ChevronDown className="w-4 h-4" />
                    </div>

                    {/* DROPDOWN MENU */}
                    {openDropdown === link.name && (
                      <div
                        className="absolute top-full left-0 mt-2 w-52 bg-white rounded-xl shadow-xl border border-gray-100 py-2 animate-fade-in"
                        onMouseEnter={() => handleMouseEnter(link.name)}
                        onMouseLeave={handleMouseLeave}
                      >
                        {link.dropdown.map((item, idx) => (
                          <Link
                            key={idx}
                            to={item.path}
                            className={`block px-4 py-2 text-sm transition ${location.pathname === item.path
                              ? 'bg-primary/10 text-primary'
                              : 'hover:bg-primary/5'
                              }`}
                          >
                            {item.name}
                          </Link>
                        ))}
                      </div>
                    )}
                  </>
                )}

              </div>
            ))}

          </nav>

          {/* MOBILE BUTTON */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-2 rounded-lg hover:bg-primary/10"
          >
            {isMobileMenuOpen ? <X /> : <Menu />}
          </button>
        </div>
        {isMobileMenuOpen && (
          <div className="lg:hidden fixed inset-x-0 top-20 bg-white/95 backdrop-blur-md shadow-xl">
            <nav className="px-4 py-6 space-y-3">

              {navLinks.map((link, i) => (
                <div key={i}>

                  {!link.dropdown && (
                    <Link
                      to={link.path}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className={`block px-4 py-3 rounded-md ${location.pathname === link.path
                        ? 'bg-primary/10 text-primary'
                        : ''
                        }`}
                    >
                      {link.name}
                    </Link>
                  )}

                  {link.dropdown && (
                    <div>
                      <p className="px-4 py-2 font-semibold text-sm text-primary">
                        {link.name}
                      </p>

                      {link.dropdown.map((item, idx) => (
                        <Link
                          key={idx}
                          to={item.path}
                          onClick={() => setIsMobileMenuOpen(false)}
                          className="block px-6 py-2 text-sm text-muted-foreground hover:text-primary"
                        >
                          {item.name}
                        </Link>
                      ))}
                    </div>
                  )}

                </div>
              ))}

            </nav>
          </div>
        )}

      </div>
    </header>
  );
};

export default Header;